import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardApi } from '../services/api';
import BottomNav from '../components/BottomNav';
import PushNotificationManager from '../components/PushNotificationManager';
import HelpGuide from '../components/HelpGuide';
import { 
  Euro, 
  CheckCircle2, 
  Clock,
  MapPin,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Camera,
  Play,
  CalendarDays,
  ArrowRight,
  Home,
  Bell,
  Star,
  Zap,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const HousekeeperDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [motivationalPhrase, setMotivationalPhrase] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  const motivationalPhrases = useMemo(() => [
    { text: "Votre travail fait la différence !", icon: <Star className="h-4 w-4" /> },
    { text: "Une journée productive vous attend !", icon: <Zap className="h-4 w-4" /> },
    { text: "Chaque effort compte, continuez !", icon: <Star className="h-4 w-4" /> },
    { text: "Vous êtes essentiel(le) à l'équipe !", icon: <Star className="h-4 w-4" /> },
    { text: "Bonne journée et bon courage !", icon: <Zap className="h-4 w-4" /> },
  ], []);

  const fetchData = useCallback(async () => {
    try {
      const res = await dashboardApi.housekeeper();
      setStats(res.data);
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const randomIndex = Math.floor(Math.random() * motivationalPhrases.length);
    setMotivationalPhrase(motivationalPhrases[randomIndex]);
    
    // Update time every minute
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, [fetchData, motivationalPhrases]);

  const getStatusConfig = (status, isUrgent) => {
    if (isUrgent && status !== 'termine') {
      return { 
        gradient: 'from-red-500 to-rose-600',
        bg: 'bg-red-50', 
        border: 'border-red-200',
        badge: 'bg-red-500 text-white',
        label: 'URGENT',
        icon: <AlertTriangle className="h-4 w-4" />
      };
    }
    switch (status) {
      case 'affectee': 
        return { 
          gradient: 'from-amber-400 to-orange-500',
          bg: 'bg-amber-50', 
          border: 'border-amber-200',
          badge: 'bg-amber-500 text-white',
          label: 'À ACCEPTER',
          icon: <Bell className="h-4 w-4" />
        };
      case 'acceptee': 
        return { 
          gradient: 'from-blue-500 to-indigo-600',
          bg: 'bg-blue-50', 
          border: 'border-blue-200',
          badge: 'bg-blue-600 text-white',
          label: 'ACCEPTÉE',
          icon: <CheckCircle2 className="h-4 w-4" />
        };
      case 'en_cours': 
        return { 
          gradient: 'from-violet-500 to-purple-600',
          bg: 'bg-violet-50', 
          border: 'border-violet-200',
          badge: 'bg-violet-600 text-white',
          label: 'EN COURS',
          icon: <Play className="h-4 w-4" />
        };
      case 'termine': 
        return { 
          gradient: 'from-emerald-500 to-teal-600',
          bg: 'bg-emerald-50', 
          border: 'border-emerald-200',
          badge: 'bg-emerald-600 text-white',
          label: 'TERMINÉE',
          icon: <CheckCircle2 className="h-4 w-4" />
        };
      default:
        return { 
          gradient: 'from-slate-400 to-slate-500',
          bg: 'bg-slate-50', 
          border: 'border-slate-200',
          badge: 'bg-slate-500 text-white',
          label: status,
          icon: null
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto">
            {/* Rotating ring */}
            <div className="absolute inset-0 rounded-full border-4 border-white/20"></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-white animate-spin"></div>
            {/* Logo center */}
            <div className="absolute inset-3 rounded-full overflow-hidden bg-white shadow-lg">
              <img src="/logo-mcb.png" alt="MCB" className="w-full h-full object-cover" />
            </div>
          </div>
          <p className="mt-6 text-white/80 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  const todayTasks = stats?.today_tasks || [];
  const pendingCount = todayTasks.filter(t => t.status !== 'termine').length;
  const greeting = currentTime.getHours() < 12 ? 'Bonjour' : currentTime.getHours() < 18 ? 'Bon après-midi' : 'Bonsoir';

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 pt-12 pb-32 px-5 overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        {/* Header Content */}
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-blue-200 text-sm font-medium">{greeting}</p>
              <h1 className="text-3xl font-bold text-white mt-1">
                {user?.name?.split(' ')[0]}
              </h1>
            </div>
            <img 
              src="/logo-mcb-full.png" 
              alt="MCB" 
              className="h-10 w-auto object-contain opacity-90"
            />
          </div>

          {/* Motivational Badge */}
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-2.5 border border-white/20">
            <span className="text-amber-300">{motivationalPhrase.icon}</span>
            <p className="text-white/90 text-sm font-medium">{motivationalPhrase.text}</p>
          </div>
        </div>
      </div>

      {/* Stats Card - Single Card */}
      <div className="px-5 -mt-20 mb-6 relative z-20">
        <div 
          className="bg-white rounded-2xl p-6 shadow-xl shadow-slate-200/50 border border-slate-100"
          onClick={() => navigate('/app/earnings')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Ce mois</p>
              <p className="text-4xl font-bold text-slate-800">{stats?.monthly_earnings || 0}€</p>
              <p className="text-sm text-slate-400 mt-2">{stats?.monthly_completed || 0} mission{(stats?.monthly_completed || 0) > 1 ? 's' : ''} terminée{(stats?.monthly_completed || 0) > 1 ? 's' : ''}</p>
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <Euro className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Push Notification Status */}
      <PushNotificationManager token={token} />

      {/* Force Update Button */}
      <div className="px-5 mb-4">
        <button
          onClick={async () => {
            toast.info('Mise à jour en cours...');
            // Clear all caches
            if ('caches' in window) {
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map(name => caches.delete(name)));
            }
            // Unregister and re-register service worker
            if ('serviceWorker' in navigator) {
              const registrations = await navigator.serviceWorker.getRegistrations();
              for (const registration of registrations) {
                await registration.unregister();
              }
            }
            // Force reload
            window.location.reload(true);
          }}
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-xl shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
          data-testid="force-update-btn"
        >
          <RefreshCw className="h-5 w-5" />
          <span className="font-semibold">Actualiser l'application</span>
        </button>
        <p className="text-xs text-center text-slate-500 mt-2">
          Cliquez pour charger la dernière version
        </p>
      </div>

      {/* Today's Date */}
      <div className="px-5 mb-4">
        <div className="flex items-center gap-3 bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
            <CalendarDays className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Aujourd'hui</p>
            <p className="text-lg font-bold text-slate-800 capitalize">
              {format(new Date(), "EEEE d MMMM", { locale: fr })}
            </p>
          </div>
          {pendingCount > 0 && (
            <div className="ml-auto">
              <span className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-full">
                {pendingCount} à faire
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Section Title */}
      <div className="px-5 mb-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800">Missions du jour</h2>
          <button 
            onClick={() => navigate('/app/tasks')}
            className="text-blue-600 text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all"
          >
            Tout voir
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Tasks List */}
      <div className="px-5 space-y-3">
        {todayTasks.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-slate-100">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-10 w-10 text-slate-300" />
            </div>
            <p className="text-slate-500 font-semibold text-lg">Aucune mission aujourd'hui</p>
            <p className="text-slate-400 text-sm mt-2">Profitez de cette journée !</p>
            <button 
              onClick={() => navigate('/app/tasks')}
              className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              Voir les missions à venir
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          todayTasks.map((task, index) => {
            const config = getStatusConfig(task.status, task.is_urgent);
            return (
              <div
                key={task.id}
                onClick={() => navigate(`/app/task/${task.id}`)}
                className={`bg-white rounded-2xl overflow-hidden shadow-sm border ${config.border} hover:shadow-lg transition-all duration-300 active:scale-[0.98]`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`task-card-${task.id}`}
              >
                {/* Status Bar */}
                <div className={`h-1.5 bg-gradient-to-r ${config.gradient}`}></div>
                
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Property Avatar */}
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-lg"
                      style={{ 
                        backgroundColor: task.property_color || '#3B82F6',
                        boxShadow: `0 8px 24px -4px ${task.property_color || '#3B82F6'}40`
                      }}
                    >
                      {task.property_name?.charAt(0) || '?'}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-bold text-slate-800 text-base truncate">
                          {task.property_name}
                        </h3>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`shrink-0 px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 ${config.badge}`}>
                            {config.icon}
                            {config.label}
                          </span>
                          {/* Teammate info */}
                          {task.housekeeper_names && task.housekeeper_names.length > 1 && (
                            <span className="text-xs text-purple-600 font-medium bg-purple-50 px-2 py-0.5 rounded">
                              En équipe avec {task.housekeeper_names.filter(n => n !== user?.name).join(', ').split(' ')[0]}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Details */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
                        {task.can_start_early && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                            Tôt OK
                          </span>
                        )}
                        {task.guest_departure_time && (
                          <span className="flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5 text-slate-400" />
                            Départ {task.guest_departure_time}
                          </span>
                        )}
                        <span className="flex items-center gap-1.5">
                          <Euro className="h-3.5 w-3.5 text-emerald-500" />
                          <span className="font-semibold text-emerald-600">{task.price || 0}€</span>
                        </span>
                      </div>

                      {/* Owner arrival warning */}
                      {task.owner_arrival && (
                        <div className="mt-2 flex items-center gap-2 bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-xs font-medium">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          {task.owner_arrival}
                        </div>
                      )}

                      {/* Address */}
                      {task.property_address && (
                        <p className="mt-2 text-xs text-slate-400 flex items-center gap-1 truncate">
                          <MapPin className="h-3 w-3 shrink-0" />
                          {task.property_address}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action hint */}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {task.photos_count > 0 && (
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Camera className="h-3.5 w-3.5" />
                          {task.photos_count} photo{task.photos_count > 1 ? 's' : ''}
                        </span>
                      )}
                      {task.checklist_progress && (
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {task.checklist_progress}
                        </span>
                      )}
                    </div>
                    <span className="text-blue-600 text-sm font-medium flex items-center gap-1">
                      Ouvrir
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Quick Actions */}
      {todayTasks.length > 0 && (
        <div className="px-5 mt-6">
          <button 
            onClick={() => navigate('/app/tasks')}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-semibold shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 transition-all flex items-center justify-center gap-2"
          >
            <CalendarDays className="h-5 w-5" />
            Voir toutes mes missions
          </button>
        </div>
      )}

      {/* Help Guide */}
      <HelpGuide />

      <BottomNav />
    </div>
  );
};

export default HousekeeperDashboard;
